import { useState } from 'react';
import AdminPage from '../components/AdminPage';
import Button from '../components/Button';
import TextInput from '../components/input/TextInput';
import List from '../components/List';
import Modal from '../components/Modal';
import { useGetUsersQuery } from './queries/getUsersQuery';
import { useCreateUserMutation } from './mutations/createUserMutation';
import { useUpdateUserMutation } from './mutations/updateUserMutation';
import { useGetRolesQuery } from './queries/getRolesQuery';

type Role = {
    id: string;
    name: string;
};

type User = {
    id: string;
    email: string;
    role?: Role;
    isActive: boolean;
};

export function AdminUsersView() {
    const [showModal, setShowModal] = useState(false);
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');

    const { data, refetch } = useGetUsersQuery();
    const { data: roleData } = useGetRolesQuery();

    const [createUser, { loading }] = useCreateUserMutation({
        variables: { input: { email, roleId } },
        onCompleted: () => {
            setEmail('');
            setRoleId('');
            setShowModal(false);
            refetch();
        },
    });

    const [updateUser] = useUpdateUserMutation({
        onCompleted: refetch,
    });

    const activeUsers = (data?.users ?? []).filter((u: User) => u.isActive);
    const inactiveUsers = (data?.users ?? []).filter((u: User) => !u.isActive);

    const handleRoleChange = (userId: string, roleId: string) => {
        updateUser({ variables: { input: { id: userId, roleId } } });
    };

    const toggleActive = (userId: string, isActive: boolean) => {
        updateUser({ variables: { input: { id: userId, isActive: !isActive } } });
    };

    return (
        <AdminPage
            title="Users"
            actions={<Button onClick={() => setShowModal(true)}>Create User</Button>}
        >
            <h2 className="text-lg font-bold mt-4 mb-2">Active Users</h2>
            <List
                items={activeUsers}
                render={(user: User) => (
                    <UserItem
                        key={user.id}
                        user={user}
                        roles={roleData?.getRoles ?? []}
                        onChangeRole={handleRoleChange}
                        onToggleActive={toggleActive}
                    />
                )}
            />

            <h2 className="text-lg font-bold mt-6 mb-2">Inactive Users</h2>
            <List
                items={inactiveUsers}
                render={(user: User) => (
                    <UserItem
                        key={user.id}
                        user={user}
                        roles={roleData?.getRoles ?? []}
                        onChangeRole={handleRoleChange}
                        onToggleActive={toggleActive}
                    />
                )}
            />

            {showModal && (
                <Modal heading="Create User" onClose={() => setShowModal(false)}>
                    <div className="space-y-2">
                        <TextInput placeholder="Email" value={email} onChange={setEmail} />

                        <div>
                            <label htmlFor="role">Role</label>
                            <select
                                id="role"
                                className="w-full border px-2 py-1 rounded"
                                value={roleId}
                                onChange={(e) => setRoleId(e.target.value)}
                            >
                                <option value="" disabled>
                                    Select a role
                                </option>
                                {roleData?.getRoles.map((role: Role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <Button onClick={() => createUser()} loading={loading}>
                            Create
                        </Button>
                    </div>
                </Modal>
            )}
        </AdminPage>
    );
}

function UserItem({
                      user,
                      roles,
                      onChangeRole,
                      onToggleActive,
                  }: {
    user: User;
    roles: Role[];
    onChangeRole: (userId: string, roleId: string) => void;
    onToggleActive: (userId: string, isActive: boolean) => void;
}) {
    return (
        <div className="flex items-center justify-between border p-2 rounded">
            <div>
                <div className="font-semibold">{user.email}</div>
                <div className="text-sm text-gray-600">
                    Role:{' '}
                    <select
                        className="border rounded px-1 py-0.5"
                        value={user.role?.id ?? ''}
                        onChange={(e) => onChangeRole(user.id, e.target.value)}
                    >
                        {roles.map((r: Role) => (
                            <option key={r.id} value={r.id}>
                                {r.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <Button
                onClick={() => onToggleActive(user.id, user.isActive)}
            >
                {user.isActive ? 'Deactivate' : 'Activate'}
            </Button>
        </div>
    );
}
